import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fieldDb } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { Calendar, type CalendarEvent } from '@/components/Calendar';

interface ExtractionRow {
  id: string;
  due_date: string | null;
  due_time: string | null;
  building_name_text: string | null;
  order_number: string | null;
  progress_state: string;
}

export function HomePage() {
  const { tenantId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<ExtractionRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      const { data } = await fieldDb()
        .from('extractions')
        .select('id, due_date, due_time, building_name_text, order_number, progress_state')
        .not('due_date', 'is', null)
        .neq('status', 'archived')
        .order('due_date', { ascending: true });
      if (cancelled) return;
      setRows((data ?? []) as ExtractionRow[]);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const events: CalendarEvent[] = useMemo(
    () =>
      rows.map((r) => {
        const date = r.due_date ?? '';
        const time = r.due_time ?? '09:00';
        return {
          id: r.id,
          title: [r.building_name_text ?? '(無題)', r.order_number].filter(Boolean).join(' '),
          start: `${date} ${time.slice(0, 5)}`,
          end: `${date} ${addHour(time, 1).slice(0, 5)}`,
          description: r.progress_state,
        };
      }),
    [rows],
  );

  return (
    <div className="h-full">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <h1 className="text-sm font-semibold">カレンダー</h1>
      </header>
      {loading ? (
        <div className="p-6 text-sm text-slate-500">読み込み中...</div>
      ) : (
        <div className="px-2 py-2">
          <Calendar
            events={events}
            onEventClick={(id) => navigate(`/extractions/${id}`)}
          />
        </div>
      )}
    </div>
  );
}

function addHour(time: string, h: number): string {
  const parts = time.split(':');
  const hh = Number(parts[0] ?? 9);
  const mm = parts[1] ?? '00';
  const next = String((hh + h) % 24).padStart(2, '0');
  return `${next}:${mm}`;
}
